/**
 * Production adapter for `set_structure_color`: the colour picker's
 * event path — `RoiColorChangeRequestedEvent`, handled by `useRoiHandlers`
 * with the SAME domain mutation as the picker's command
 * (`RoiService.setColor`), which also refreshes the list and the drawing.
 * Like the picker's command (`useRoiCommands`), a change arms the
 * unsaved-changes guard.
 *
 * Deliberately avoids the picker's command hooks (`useRoiCommands`,
 * `useRoiListView`): they require a loaded patient session and throw
 * without one, and the chat drawer is mounted even before a patient is
 * loaded. The live colour is read through the null-safe session accessor.
 * Browser glue covered by the manual demo; the logic is unit-tested through
 * fakes.
 */
import { useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { domainEventBus, RoiColorChangeRequestedEvent } from '~/domain/events';
import { environmentState } from '~/state/atoms/engineAtoms';
import { hasUnsavedChangesState } from '~/state/atoms/patientAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { projectStructureFacts } from '../core/engineStructureFacts';
import type { ColorDeps } from './toolRunner';

export function useStructureColorDeps(): () => Omit<
    ColorDeps,
    'pendingMutationExists'
> {
    const env = useRecoilValue(environmentState);
    const structureSet = useRecoilValue(currentStructureSetState);
    const session = useSessionOrNull();
    const setHasUnsavedChanges = useSetRecoilState(hasUnsavedChangesState);

    return useCallback(() => {
        const uid = structureSet?.dicomUid;
        const liveRoi = (roiNumber: number) =>
            uid
                ? session?.patient
                      .getStructureSetWithSopInstanceUid(uid)
                      ?.rois.find((r) => r.roiNumber === roiNumber)
                : undefined;
        return {
            structures: projectStructureFacts(structureSet, env),
            canModify: (roiNumber: number) =>
                env?.getRoi(roiNumber)?.canModify ?? false,
            currentColor: (roiNumber: number) => {
                const c = liveRoi(roiNumber)?.color;
                return c ? { R: c.R, G: c.G, B: c.B } : undefined;
            },
            currentContextId: () => env?.structureSet?.sopInstanceUID,
            setColor: async (roiNumber, color) => {
                // Awaited so the read-back sees the result.
                await domainEventBus.publish(
                    new RoiColorChangeRequestedEvent(
                        roiNumber,
                        { ...color },
                        'user'
                    )
                );
                setHasUnsavedChanges(true);
            },
        };
    }, [env, structureSet, session, setHasUnsavedChanges]);
}
